import { Post } from "@/types/post";

export type LatLng = { lat: number; lng: number };

export type GeoResult =
  | { kind: "ok"; position: LatLng }
  | { kind: "denied" }
  | { kind: "unavailable" }
  | { kind: "timeout" };

export const GEO_RESOLVE_TIMEOUT_MS = 8000;

export const hasGeoPoint = (
  p: Post,
): p is Post & {
  location: { geoPoint: { latitude: number; longitude: number } };
} =>
  !!(
    p.location?.geoPoint &&
    typeof p.location.geoPoint.latitude === "number" &&
    typeof p.location.geoPoint.longitude === "number"
  );

export const requestBrowserPosition = (): Promise<GeoResult> =>
  new Promise((resolve) => {
    if (
      typeof navigator === "undefined" ||
      !navigator.geolocation ||
      typeof navigator.geolocation.getCurrentPosition !== "function"
    ) {
      resolve({ kind: "unavailable" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          kind: "ok",
          position: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        }),
      (err) => {
        if (err?.code === 1) resolve({ kind: "denied" });
        else if (err?.code === 3) resolve({ kind: "timeout" });
        else resolve({ kind: "unavailable" });
      },
      { timeout: GEO_RESOLVE_TIMEOUT_MS, maximumAge: 60_000 },
    );
  });

const EARTH_RADIUS_M = 6_371_000;
const toRadians = (deg: number) => (deg * Math.PI) / 180;

export const haversineMeters = (a: LatLng, b: LatLng): number => {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
};
